/**
 * Back to the same place after a full screen video (round 2 #4): in Android in-app browsers (Zalo's WebView) leaving
 * full screen (the Testimonials player, the Blog's YouTube player) put the page back at the top. Not reproduced on
 * iOS / Safari / Chrome, where this does nothing (the page is already where it was).
 * - The place: the page's scroll from before the full screen. Entering can move the page already (the video's box
 *   takes the viewport), so it is the last scroll at rest SETTLE ms before full screen, not the one at the event.
 * - Leaving: the page goes back there, and for GUARD ms after any jump away (the WebView resizing back, ScrollTrigger's
 *   refresh) is taken back too. A touch, wheel or key stops the guard: the visitor scrolls on their own.
 * - Covers every full screen on the page: a <video>, or an iframe (YouTube's button makes the iframe full screen, and
 *   the page gets the event too). Lenis gets the scroll (window.lenis), else a plain window.scrollTo.
 */

const SETTLE = 300; // ms: scroll positions this close before full screen may already be the jump
const GUARD = 1500; // ms after leaving full screen while jumps are taken back
const MOVED = 50; // px off the place that counts as a jump

type FullscreenDocument = Document & { webkitFullscreenElement?: Element | null };

const fullscreenElement = (): Element | null =>
	document.fullscreenElement ?? (document as FullscreenDocument).webkitFullscreenElement ?? null;

const scrollToY = (y: number): void => {
	if (window.lenis) {
		window.lenis.scrollTo(y, { immediate: true, force: true });
	} else {
		window.scrollTo(0, y);
	}
};

export function initFullscreenScroll(): void {
	const history: { time: number; y: number }[] = []; // recent scroll positions outside full screen
	let saved: number | null = null; // the place to come back to
	let guardUntil = 0;

	const record = (): void => {
		const now = performance.now();
		history.push({ time: now, y: window.scrollY });
		while (history.length > 1 && now - history[0].time > SETTLE * 4) {
			history.shift();
		}
	};
	record();

	const restore = (): void => {
		if (saved !== null && Math.abs(window.scrollY - saved) > MOVED) {
			scrollToY(saved);
		}
	};

	window.addEventListener(
		'scroll',
		() => {
			if (fullscreenElement()) {
				return;
			}
			if (saved !== null && performance.now() < guardUntil) {
				restore();
				return;
			}
			record();
		},
		{ passive: true },
	);

	const change = (): void => {
		if (fullscreenElement()) {
			if (saved === null) {
				const before = performance.now() - SETTLE;
				saved = [...history].reverse().find((entry) => entry.time <= before)?.y ?? history[0]?.y ?? window.scrollY;
			}
			return;
		}
		if (saved === null) {
			return;
		}
		guardUntil = performance.now() + GUARD;
		restore();
		requestAnimationFrame(restore);
		const place = saved;
		window.setTimeout(() => {
			if (saved === place) {
				restore();
				saved = null;
				history.length = 0;
				record();
			}
		}, GUARD);
	};
	document.addEventListener('fullscreenchange', change);
	document.addEventListener('webkitfullscreenchange', change);

	// The visitor scrolls on their own: stop taking the page back
	const release = (): void => {
		guardUntil = 0;
	};
	['touchstart', 'wheel', 'keydown'].forEach((type) => window.addEventListener(type, release, { passive: true }));
	window.addEventListener('resize', () => performance.now() < guardUntil && restore());
}
